import { createSlice } from '@reduxjs/toolkit';
import { queryTwitterMention } from 'api/platformMention';
import camelize from 'camelize';
import Form from 'components/shared/Form';
import Tooltip from 'components/shared/Tooltip';
import flashMessage from 'helpers/flashMessage';
import { useIntl } from 'hooks';
import { IconLoader, IconSearch } from 'icons';
import React, { useReducer, useRef, useState } from 'react';
import TwitterSearchResult from 'types/TwitterSearchResult';
import { useDebouncedCallback } from 'use-debounce/lib';
import UpdateUserTwitterSearchResults from './UpdateUserTwitterSearchResults';

interface Props {
  onSelect: (result: TwitterSearchResult) => void;
  isSaving?: boolean;
  autoFocus?: boolean;
}

const initialState = {
  searchResults: [] as TwitterSearchResult[],
  isSearching: false,
  noResults: false,
  query: '',
};

const { reducer, actions } = createSlice({
  initialState,
  name: 'UpdateUserTwitterSearchReducer',
  reducers: {
    searchStart: (state) => {
      state.isSearching = true;
      state.noResults = false;
      state.searchResults = [];
    },
    searchStop: (state) => {
      state.isSearching = false;
    },
    searchSuccess: (state, action: { payload: TwitterSearchResult[] }) => {
      state.searchResults = action.payload;
      if (!action.payload.length) state.noResults = true;
    },
    searchClear: (state) => {
      state.isSearching = false;
      state.noResults = false;
      state.searchResults = [];
    },
    updateQuery: (state, action: { payload: string }) => {
      state.query = action.payload;
    },
  },
});

export default function UpdateUserTwitterSearch(props: Props) {
  const { onSelect, isSaving, autoFocus } = props;
  const { t } = useIntl();
  const inputRef = useRef<HTMLInputElement>(null);
  const [state, dispatch] = useReducer(reducer, initialState);
  const { searchResults, noResults, isSearching, query } = state;
  const [isFocused, setIsFocused] = useState(false);

  const handleSearch = async (value?: string) => {
    let searchTerm = (value || query).trim();
    if (!searchTerm || searchTerm === '@') return;

    dispatch(actions.searchStart());

    try {
      const data = camelize(await queryTwitterMention(searchTerm, 'user'));
      const results = data || [];
      dispatch(actions.searchSuccess(results));
    } catch (e) {
      console.error(e);
      flashMessage('Global__UnexpectedError', { isError: true });
    } finally {
      dispatch(actions.searchStop());
    }
  };

  const [debouncedOnChange] = useDebouncedCallback(
    (val: string) => handleSearch(val),
    500
  );

  const handleChange = (value: string) => {
    dispatch(actions.updateQuery(value));

    if (!!value.trim()) {
      debouncedOnChange(value);
    } else {
      dispatch(actions.searchClear());
    }
  };

  return (
    <Tooltip
      trigger="manual"
      visible={isFocused && (!!searchResults.length || noResults)}
      hideOnClick={false}
      interactive
      arrow={false}
      theme="socialieNoPad"
      maxWidth="none"
      placement="bottom-start"
      content={
        <div onClick={(e) => e.stopPropagation()}>
          <UpdateUserTwitterSearchResults
            searchResults={searchResults}
            noResults={noResults}
            onSave={onSelect}
            width={inputRef.current?.clientWidth}
            isSaving={isSaving}
          />
        </div>
      }
    >
      <div>
        <Form.TextInput
          ref={inputRef}
          value={state.query}
          autoFocus={autoFocus}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onClick={(e) => e.stopPropagation()}
          leftIcon={isSearching ? IconLoader : IconSearch}
          placeholder={t('UpdateUserTwitterSearch__Placeholder')}
          skipField
        />
      </div>
    </Tooltip>
  );
}
